import {useCallback} from 'react';

import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * null - table is not sorted
 * index - column the table is sorted by
 * descending - table sorted by the `index`th column from low-to-high (high-to-low if false)
 */
export type RunQueryOutputTableSortState = null | {index: number; descending: boolean};

interface UseRunQueryOutputTableSortState {
  sortState: RunQueryOutputTableSortState;
  toggleSort: (columnIndex: number) => void;
}

export const runQueryOutputTableSortState = makeUseGlobalClientState<RunQueryOutputTableSortState>(null);

/**
 * Hook that manages the sort state of SortableRunQueryOutputTable.
 *
 * This hook allows callers to manage the state of SortableRunQueryOutputTable on the client and returns the
 * state and callback that that can be used as input props with SortableRunQueryOutputTable. This state is
 * globally persisted and accessible on the client, so different components can import RunQueryOutputTableSortState
 * directly if they want to use the shared state for some other purpose.
 */
export default function useRunQueryOutputTableSortState(
  tableViewStateID: string
): UseRunQueryOutputTableSortState {
  const [sortState, setSortState] = runQueryOutputTableSortState.useGlobalClientState(tableViewStateID);

  const toggleSort = useCallback(
    (columnIndex: number) => {
      if (sortState == null || columnIndex !== sortState.index) {
        // If nothing is being sorted yet, or the user is trying to sort a different column
        // than the one previously being sorted by, switch to sorting by the new column in
        // descending order.
        setSortState({index: columnIndex, descending: true});
      } else {
        // If clicking the sort button again on a column already being sorted,
        // switch from sorting in descending -> ascending -> not sorting order
        setSortState(sortState.descending ? {index: columnIndex, descending: false} : null);
      }
    },
    [sortState, setSortState]
  );

  return {
    sortState,
    toggleSort,
  };
}
